import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { Upload, FileText, Camera, Mic, Volume2, BookOpen, X } from 'lucide-react';
import { useSpeechSynthesis } from 'react-speech-kit';
import WordList from '../components/WordList';
import { extractWordsFromText, extractTextFromImage } from '../utils/textExtractor';
import { translateWord, getSimilarWords, generateExampleSentence } from '../utils/wordService';

const ExtractorContainer = styled.div`
  padding: 40px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: bold;
  color: #1a202c;
  margin-bottom: 30px;
  text-align: center;
`;

const InputSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InputCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UploadArea = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$isDragOver ? '#f3f4f6' : 'white'};

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const UploadIcon = styled.div`
  width: 60px;
  height: 60px;
  background: #eff6ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #3b82f6;
`;

const UploadText = styled.p`
  color: #6b7280;
  margin-bottom: 20px;
`;

const FileInput = styled.input`
  display: none;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$variant === 'primary' ? '#3b82f6' : '#f3f4f6'};
  color: ${props => props.$variant === 'primary' ? 'white' : '#374151'};

  &:hover {
    background: ${props => props.$variant === 'primary' ? '#2563eb' : '#e5e7eb'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingContent = styled.div`
  background: white;
  padding: 40px;
  border-radius: 16px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PreviewImage = styled.div`
  position: relative;
  margin-top: 20px;
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SectionCard = styled.section`
  background: rgba(255,255,255,0.18);
  border-radius: 24px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.18);
  backdrop-filter: blur(12px);
  border: 1.5px solid rgba(255,255,255,0.25);
  color: #22223b;
  padding: 32px;
  margin-bottom: 32px;
  transition: box-shadow 0.2s;
`;

function WordExtractor() {
  const [text, setText] = useState('');
  const [words, setWords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const { speak } = useSpeechSynthesis();

  const handleFileUpload = async (file) => {
    if (!file) return;

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    setIsLoading(true);
    try {
      const extractedText = await extractTextFromImage(file);
      const extractedWords = await extractWordsFromText(extractedText);
      setWords(extractedWords);
    } catch (error) {
      console.error('Error extracting text:', error);
      alert('텍스트 추출 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextExtract = async () => {
    if (!text.trim()) return;

    setIsLoading(true);
    try {
      const extractedWords = await extractWordsFromText(text);
      setWords(extractedWords);
    } catch (error) {
      console.error('Error extracting words:', error);
      alert('단어 추출 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileUpload(file);
    }
  };

  const removeImage = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setWords([]);
  };

  const handleWordClick = async (word) => {
    try {
      // 단어 번역
      const translation = await translateWord(word.text);

      // 유사어 추출
      const similarWords = await getSimilarWords(word.text);

      // 예문 생성
      const exampleSentence = await generateExampleSentence(word.text);

      // 단어 정보 업데이트
      const updatedWord = {
        ...word,
        translation,
        similarWords,
        exampleSentence,
        clicked: true,
        clickedAt: new Date().toISOString()
      };

      setWords(prevWords =>
        prevWords.map(w => w.id === word.id ? updatedWord : w)
      );

      // 히스토리에 추가
      const history = JSON.parse(localStorage.getItem('wordHistory') || '[]');
      history.unshift(updatedWord);
      localStorage.setItem('wordHistory', JSON.stringify(history.slice(0, 100))); // 최대 100개 저장

    } catch (error) {
      console.error('Error processing word:', error);
    }
  };

  const speakWord = (word, text) => {
    speak({ text: text || word, rate: 0.8, pitch: 1, type: 'sentence' });
  };

  return (
    <ExtractorContainer>
      <Title>단어 추출</Title>

      <InputSection>

        <InputCard>
          <CardTitle>
            <FileText size={24} />
            텍스트에서 추출
          </CardTitle>

          <TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="문장을 입력하거나 복사해서 붙여넣으세요..."
          />

          <ButtonGroup>
            <Button
              $variant="primary"
              onClick={handleTextExtract}
              disabled={!text.trim() || isLoading}
            >
              <BookOpen size={16} />
              단어 추출
            </Button>
            <Button onClick={() => setText('')}>
              <X size={16} />
              지우기
            </Button>
          </ButtonGroup>
        </InputCard>
      </InputSection>

      {words.length > 0 && (
        <WordList
          words={words}
          onWordClick={handleWordClick}
          onSpeak={speakWord}
        />
      )}

      {isLoading && (
        <LoadingOverlay>
          <LoadingContent>
            <LoadingSpinner />
            <p>단어를 추출하고 있습니다...</p>
          </LoadingContent>
        </LoadingOverlay>
      )}
    </ExtractorContainer>
  );
}

export default WordExtractor;
